package com.mps.asc_app.ui.screens

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Error
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import com.mps.asc_app.config.DebugRegistrationConfig
import com.mps.asc_app.data.places.model.AddressSuggestion
import com.mps.asc_app.presentation.MainUiState
import com.mps.asc_app.ui.components.ASCSectionCard
import com.mps.asc_app.util.PRIVACY_CONSENT_TEXT

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RegisterScreen(
    state: MainUiState,
    onAddressQueryChanged: (String) -> Unit,
    onAddressSuggestionSelected: (AddressSuggestion) -> Unit,
    onRegister: (email: String, password: String, vorname: String, nachname: String, telefonnummer: String, privacyAccepted: Boolean) -> Unit,
    onCreateDebugUserClick: (() -> Unit)? = null,
    onBackToLogin: () -> Unit,
    isLoading: Boolean = false,
    errorMessage: String? = null,
    contentPadding: PaddingValues = PaddingValues(0.dp)
) {
    var email by rememberSaveable { mutableStateOf("") }
    var vorname by rememberSaveable { mutableStateOf("") }
    var nachname by rememberSaveable { mutableStateOf("") }
    var telefonnummer by rememberSaveable { mutableStateOf("") }
    var password by rememberSaveable { mutableStateOf("") }
    var passwordRepeat by rememberSaveable { mutableStateOf("") }
    var localAddressQuery by rememberSaveable { mutableStateOf(state.addressQuery) }

    var privacyAccepted by rememberSaveable { mutableStateOf(false) }
    var showPrivacyDialog by rememberSaveable { mutableStateOf(false) }

    var showPassword by rememberSaveable { mutableStateOf(false) }
    var showPasswordRepeat by rememberSaveable { mutableStateOf(false) }

    val passwordsMatch = password == passwordRepeat
    val canSubmit =
        email.isNotBlank() &&
                password.isNotBlank() &&
                passwordsMatch &&
                state.isAddressValid &&
                privacyAccepted &&
                !state.isLoading &&
                !isLoading

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(contentPadding)
            .background(MaterialTheme.colorScheme.background)
            .padding(horizontal = 16.dp, vertical = 20.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .widthIn(max = 760.dp),
            shape = RoundedCornerShape(24.dp),
            color = MaterialTheme.colorScheme.surface,
            tonalElevation = 2.dp,
            shadowElevation = 4.dp,
        ) {
            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(20.dp),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
            item {
                Text(
                    text = "Registrieren",
                    style = MaterialTheme.typography.headlineSmall
                )
                Text(
                    text = "Erstelle dein Konto, um Plätze zu buchen.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }

            item {
                AnimatedVisibility(visible = errorMessage != null) {
                    Surface(
                        color = MaterialTheme.colorScheme.errorContainer,
                        contentColor = MaterialTheme.colorScheme.onErrorContainer,
                        shape = RoundedCornerShape(12.dp),
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(horizontal = 12.dp, vertical = 10.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Icon(
                                imageVector = Icons.Filled.Error,
                                contentDescription = null
                            )
                            Text(errorMessage.orEmpty(), style = MaterialTheme.typography.bodySmall)
                        }
                    }
                }
            }

            item {
                ASCSectionCard {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Text(
                            text = "Kontaktdaten",
                            style = MaterialTheme.typography.titleMedium
                        )

                        OutlinedTextField(
                            value = email,
                            onValueChange = { email = it },
                            label = { Text("E-Mail") },
                            singleLine = true,
                            enabled = !isLoading,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                            modifier = Modifier.fillMaxWidth()
                        )

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            OutlinedTextField(
                                value = vorname,
                                onValueChange = { vorname = it },
                                label = { Text("Vorname") },
                                singleLine = true,
                                enabled = !isLoading,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
                                modifier = Modifier.weight(1f)
                            )
                            OutlinedTextField(
                                value = nachname,
                                onValueChange = { nachname = it },
                                label = { Text("Nachname") },
                                singleLine = true,
                                enabled = !isLoading,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
                                modifier = Modifier.weight(1f)
                            )
                        }

                        OutlinedTextField(
                            value = telefonnummer,
                            onValueChange = { telefonnummer = it },
                            label = { Text("Telefonnummer") },
                            singleLine = true,
                            enabled = !isLoading,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
            }

            item {
                ASCSectionCard {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        Text(
                            text = "Adresse",
                            style = MaterialTheme.typography.titleMedium
                        )

                        OutlinedTextField(
                            value = localAddressQuery,
                            onValueChange = {
                                localAddressQuery = it
                                onAddressQueryChanged(it)
                            },
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text),
                            label = { Text("Straße, Hausnummer, Ort") },
                            singleLine = true,
                            enabled = !isLoading,
                            isError = state.addressError != null,
                            supportingText = {
                                when {
                                    state.addressError != null ->
                                        Text(state.addressError ?: "")

                                    state.isAddressValid ->
                                        Text("Adresse übernommen")

                                    else ->
                                        Text("Tippe, um Vorschläge zu erhalten")
                                }
                            },
                            trailingIcon = {
                                when {
                                    state.addressError != null ->
                                        Icon(Icons.Filled.Error, contentDescription = null)

                                    state.isAddressValid ->
                                        Icon(Icons.Filled.CheckCircle, contentDescription = null)

                                    else -> Unit
                                }
                            },
                            modifier = Modifier.fillMaxWidth()
                        )

                        LaunchedEffect(state.addressQuery, state.isAddressValid) {
                            if (state.isAddressValid && state.addressQuery.isNotBlank() && state.addressQuery != localAddressQuery) {
                                localAddressQuery = state.addressQuery
                            }
                        }

                        // Suggestions as “dropdown-like” list
                        AnimatedVisibility(visible = state.addressSuggestions.isNotEmpty()) {
                            Surface(
                                tonalElevation = 1.dp,
                                shape = RoundedCornerShape(14.dp),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Column(
                                    modifier = Modifier.padding(vertical = 6.dp)
                                ) {
                                    state.addressSuggestions.take(5).forEach { suggestion ->
                                        ListItem(
                                            headlineContent = { Text(suggestion.fullText) },
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .clickable(enabled = !isLoading) {
                                                    localAddressQuery = suggestion.fullText
                                                    onAddressSuggestionSelected(suggestion)
                                                }
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }

            item {
                ASCSectionCard {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Text(
                            text = "Sicherheit",
                            style = MaterialTheme.typography.titleMedium
                        )

                        OutlinedTextField(
                            value = password,
                            onValueChange = { password = it },
                            label = { Text("Passwort") },
                            singleLine = true,
                            enabled = !isLoading,
                            visualTransformation = if (showPassword) VisualTransformation.None else PasswordVisualTransformation(),
                            trailingIcon = {
                                IconButton(onClick = { showPassword = !showPassword }) {
                                    Icon(
                                        imageVector = if (showPassword) Icons.Filled.VisibilityOff else Icons.Filled.Visibility,
                                        contentDescription = null
                                    )
                                }
                            },
                            modifier = Modifier.fillMaxWidth()
                        )

                        OutlinedTextField(
                            value = passwordRepeat,
                            onValueChange = { passwordRepeat = it },
                            label = { Text("Passwort wiederholen") },
                            singleLine = true,
                            enabled = !isLoading,
                            isError = passwordRepeat.isNotEmpty() && !passwordsMatch,
                            supportingText = {
                                if (passwordRepeat.isNotEmpty() && !passwordsMatch) {
                                    Text("Passwörter stimmen nicht überein")
                                }
                            },
                            visualTransformation = if (showPasswordRepeat) VisualTransformation.None else PasswordVisualTransformation(),
                            trailingIcon = {
                                IconButton(onClick = { showPasswordRepeat = !showPasswordRepeat }) {
                                    Icon(
                                        imageVector = if (showPasswordRepeat) Icons.Filled.VisibilityOff else Icons.Filled.Visibility,
                                        contentDescription = null
                                    )
                                }
                            },
                            modifier = Modifier.fillMaxWidth()
                        )
                    }
                }
            }

            item {
                ASCSectionCard {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(16.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        Text(
                            text = "Datenschutz",
                            style = MaterialTheme.typography.titleMedium
                        )

                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(10.dp)
                        ) {
                            Checkbox(
                                checked = privacyAccepted,
                                onCheckedChange = { checked -> privacyAccepted = checked },
                                enabled = !isLoading,
                            )
                            Text(
                                text = "Ich stimme der Datenschutzerklaerung zu.",
                                modifier = Modifier.weight(1f)
                            )
                            TextButton(
                                onClick = { showPrivacyDialog = true },
                                enabled = !isLoading
                            ) {
                                Text("Lesen")
                            }
                        }

                        if (!privacyAccepted) {
                            Text(
                                text = "Ohne Zustimmung ist keine Registrierung moeglich.",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }
            }
            item {
                Spacer(modifier = Modifier.height(2.dp))
                Button(
                    onClick = { onRegister(email.trim(), password, vorname, nachname, telefonnummer, privacyAccepted) },
                    enabled = canSubmit,
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(52.dp),
                    shape = RoundedCornerShape(14.dp)
                ) {
                    if (isLoading) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(18.dp),
                            strokeWidth = 2.dp
                        )
                        Spacer(Modifier.width(10.dp))
                        Text("Registrieren …")
                    } else {
                        Text("Registrieren")
                    }
                }

                OutlinedButton(
                    onClick = onBackToLogin,
                    enabled = !isLoading,
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(48.dp)
                ) {
                    Text("Zurück zum Login")
                }

                if (DebugRegistrationConfig.enabled && onCreateDebugUserClick != null) {
                    OutlinedButton(
                        onClick = onCreateDebugUserClick,
                        enabled = !isLoading,
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(48.dp)
                    ) {
                        Text("Testuser automatisch anlegen")
                    }
                }
            }



            item { Spacer(modifier = Modifier.height(6.dp)) }
            }
        }
    }

    if (showPrivacyDialog) {
        AlertDialog(
            onDismissRequest = { showPrivacyDialog = false },
            confirmButton = {
                TextButton(onClick = { showPrivacyDialog = false }) {
                    Text("Schliessen")
                }
            },
            title = { Text("Datenschutzerklaerung") },
            text = {
                Text(
                    text = PRIVACY_CONSENT_TEXT,
                    style = MaterialTheme.typography.bodySmall
                )
            }
        )
    }

}